import { useEffect, useRef, useState } from 'react';
import { HOME_WIDGET_EXIT_MS } from './widgetLayoutConstants';

type WidgetLayoutTransitionState = {
  layoutIds: string[];
  exitingIds: Set<string>;
  enteringIds: Set<string>;
};

const useWidgetLayoutTransition = (
  visibleIds: string[]
): WidgetLayoutTransitionState => {
  const [layoutIds, setLayoutIds] = useState(visibleIds);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());
  const previousVisibleIdsRef = useRef(visibleIds);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const previousVisibleIds = previousVisibleIdsRef.current;
    const removedIds = previousVisibleIds.filter(
      (id) => !visibleIds.includes(id)
    );
    const addedIds = visibleIds.filter(
      (id) => !previousVisibleIds.includes(id)
    );

    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (removedIds.length === 0 && addedIds.length === 0) {
      setLayoutIds(visibleIds);
      previousVisibleIdsRef.current = visibleIds;
      return undefined;
    }

    setExitingIds(new Set(removedIds));
    setEnteringIds(new Set(addedIds));
    setLayoutIds(Array.from(new Set([...visibleIds, ...removedIds])));

    exitTimerRef.current = setTimeout(() => {
      setLayoutIds(visibleIds);
      setExitingIds(new Set());
      setEnteringIds(new Set());
      exitTimerRef.current = null;
    }, HOME_WIDGET_EXIT_MS);

    previousVisibleIdsRef.current = visibleIds;

    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, [visibleIds]);

  return { layoutIds, exitingIds, enteringIds };
};

export default useWidgetLayoutTransition;
